import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminPlatform,
  AdminQueryEnvelope,
  AdminReleaseRegression,
  AdminReleaseRegressionQuery,
} from "../../contracts/admin-read-models";

export type AdminReleaseHotspot = {
  release: string;
  appVersion?: string;
  platform?: AdminPlatform;
  topErrorCode?: string;
  drilldownTo: string;
};

function findDominantPlatform(
  concentration: Partial<Record<AdminPlatform, number>> | undefined,
): AdminPlatform | undefined {
  if (!concentration) {
    return undefined;
  }

  const entries = Object.entries(concentration) as Array<[AdminPlatform, number]>;
  entries.sort((left, right) => right[1] - left[1]);
  return entries[0]?.[0];
}

function buildFailedFlowsDrilldown(paramsInput: {
  release: string;
  appVersion?: string;
  platform?: AdminPlatform;
  topErrorCode?: string;
}): string {
  const params = new URLSearchParams();
  params.set("release", paramsInput.release);

  if (paramsInput.appVersion) {
    params.set("appVersion", paramsInput.appVersion);
  }

  if (paramsInput.platform) {
    params.set("platform", paramsInput.platform);
  }

  if (paramsInput.topErrorCode) {
    params.set("errorCode", paramsInput.topErrorCode);
  }

  return `/admin/failed-flows?${params.toString()}`;
}

export async function loadReleaseRegression(
  port: AdminQueryPort,
  query: AdminReleaseRegressionQuery,
): Promise<AdminQueryEnvelope<AdminReleaseRegression>> {
  return port.getReleaseRegression(query);
}

export function findReleaseRegressionHotspots(
  regression: AdminReleaseRegression,
): AdminReleaseHotspot[] {
  return regression.items.map((item) => {
    const platform = findDominantPlatform(item.platformConcentration);

    return {
      release: item.release,
      appVersion: item.appVersion,
      platform,
      topErrorCode: item.topErrorCode,
      drilldownTo: buildFailedFlowsDrilldown({
        release: item.release,
        appVersion: item.appVersion,
        platform,
        topErrorCode: item.topErrorCode,
      }),
    };
  });
}
